// Authenticated layout with Sider, Header, and Content
import { useState, useMemo, useEffect, useRef } from 'react';
import { Badge, Drawer, Dropdown, Empty, Form, Input, Layout, Menu, Modal, Spin, Tag, Button, Tooltip } from 'antd';
import {
    DashboardOutlined,
    UserOutlined,
    FileTextOutlined,
    ShoppingOutlined,
    InboxOutlined,
    TeamOutlined,
    CarOutlined,
    DollarOutlined,
    BarChartOutlined,
    SettingOutlined,
    TagOutlined,
    BankOutlined,
    DownOutlined,
    LogoutOutlined,
    NotificationOutlined,
    CustomerServiceOutlined,
    MenuOutlined,
    CloseOutlined,
    CalendarOutlined,
    ClockCircleOutlined,
    AppstoreOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '@stores/auth.store';
import { useLocation, useNavigate } from 'react-router-dom';
import { getDefaultGuestSignInPath } from '@utils/routing';
import { getMenuItemsByRole, getRoutesByRole } from '@routes/getRoutes';
import { isOwnerRole } from '@utils/routing';
import { useDocTitle } from '@hooks/useDocTitle';
import {
    LayoutContainer,
    MainContentArea,
    BodyContainer,
    ContentWrapper,
    HeaderWrapper,
    HeaderContent,
    HeaderTitle,
    HeaderActions,
    HeaderIconButton,
    HeaderUserButton,
    UserMenuMeta,
    UserMenuEmail,
    UserMenuRole,
    SiderWrapper,
    StickyTopSection,
    LogoContainer,
    LogoImage,
    SchoolBrandBlock,
    SchoolNameText,
    SchoolMetaRow,
    SchoolRegionText,
    UserAvatar,
    MenuContainer,
    MobileDrawerButton,
    MobileDrawer,
    DrawerOverlay,
    DrawerContent,
    DrawerHeader,
    DrawerCloseButton,
    SidebarFooter,
    FooterBrandRow,
    FooterBrandLogo,
    FooterBrandText,
    FooterBrandTitle,
    FooterVersionText,
    DrawerFooter,
    HeaderDivider,
    PlanCard,
    PlanCardMeta,
    PlanCardLabel,
    PlanCardName,
    PlanUpgradeButton,
} from './style';
import styled from 'styled-components';
import { APP_VERSION } from '../constants/appMeta';
import SchoolProfilePanel from '@components/tenant/SchoolProfilePanel';
import { useTenantNotifications } from '@hooks/tenant/useTenantNotifications';
import { subscribeTenantDocument } from '@services/registry';
import NotAuthorized from '@/pages/outside/NotAuthorized';

const { Sider } = Layout;

const TENANT_MODULES = [
    { key: 'students', label: 'Students', paths: ['/tenant/students'] },
    { key: 'teachers', label: 'Teachers', paths: ['/tenant/teachers'] },
    { key: 'staff', label: 'Staff', paths: ['/tenant/staff'] },
    { key: 'classes', label: 'Classes', paths: ['/tenant/classes-subjects'] },
    { key: 'badges', label: 'Badges', paths: ['/tenant/classes-subjects'] },
    { key: 'schoolTime', label: 'School Time', paths: ['/tenant/school-time'] },
    { key: 'notices', label: 'Notices', paths: ['/tenant/notices-announcements'] },
    { key: 'homeworks', label: 'Homework Diary', paths: ['/tenant/homeworks'] },
    { key: 'attendance_students', label: 'Student Attendance', paths: ['/tenant/attendance/students'] },
    { key: 'attendance_teachers', label: 'Teacher Attendance', paths: ['/tenant/attendance/teachers'] },
    { key: 'attendance_staff', label: 'Staff Attendance', paths: ['/tenant/attendance/staff'] },
    { key: 'finance_accounts', label: 'Account Management', paths: ['/tenant/finance/accounts'] },
    { key: 'finance_transaction', label: 'Transaction Ledger', paths: ['/tenant/finance/transaction-ledger'] },
    { key: 'finance_receive', label: 'Receive Ledger', paths: ['/tenant/finance/receive-ledger'] },
    { key: 'finance_expense', label: 'Expense Ledger', paths: ['/tenant/finance/expense-ledger'] },
    { key: 'fees', label: 'Fees Management', paths: ['/tenant/fees-management'] },
];

const Inside = ({ children }) => {
    const user = useAuthStore((state) => state.user);
    const role = useAuthStore((state) => state.role);
    const permissions = useAuthStore((state) => state.permissions);
    const logout = useAuthStore((state) => state.logout);
    const location = useLocation();
    const navigate = useNavigate();
    const userRole = role || user?.role || null;
    const effectiveRole = userRole;
    const tenantId = user?.tenantId || null;
    const userUid = user?.uid || null;

    const [tenantDoc, setTenantDoc] = useState(null);

    useEffect(() => {
        if (effectiveRole === 'tenant' && tenantId) {
            const unsub = subscribeTenantDocument({
                tenantId,
                onNext: (doc) => {
                    setTenantDoc(doc);
                },
                onError: (err) => {
                    console.error('Error subscribing to tenant document in Inside.jsx:', err);
                }
            });
            return () => unsub();
        } else {
            setTenantDoc(null);
        }
    }, [effectiveRole, tenantId]);
    const [userDropdownOpen, setUserDropdownOpen] = useState(false);
    const [notificationDropdownOpen, setNotificationDropdownOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [profileOpen, setProfileOpen] = useState(false);
    const [upgradeOpen, setUpgradeOpen] = useState(false);
    const [openKeys, setOpenKeys] = useState([]);
    const bodyContainerRef = useRef(null);
    useDocTitle({ location });

    // Handle scroll for glassy header effect on body container
    useEffect(() => {
        const handleScroll = () => {
            const scrollElement = bodyContainerRef.current;
            if (scrollElement) {
                setIsScrolled(scrollElement.scrollTop > 0);
            }
        };

        const scrollElement = bodyContainerRef.current;

        if (scrollElement) {
            scrollElement.addEventListener('scroll', handleScroll, { passive: true });
            // Check initial scroll position
            handleScroll();
        }

        return () => {
            if (scrollElement) {
                scrollElement.removeEventListener('scroll', handleScroll);
            }
        };
    }, []);

    // Get user initials for avatar
    const getUserInitials = () => {
        if (user?.name) {
            return user.name
                .split(' ')
                .map(n => n[0])
                .join('')
                .toUpperCase()
                .slice(0, 2);
        }
        return user?.email?.[0]?.toUpperCase() || 'U';
    };

    // Get user display name
    const getUserName = () => {
        return user?.name || user?.email?.split('@')[0] || 'User';
    };

    // Get user role
    const getUserRole = () => {
        return user?.role || 'User';
    };

    const getUserEmail = () => {
        return user?.email || '—';
    };

    const schoolName = user?.schoolName || '—';
    const schoolRegion = user?.region || '—';
    const schoolPlan = user?.plan || '—';
    const schoolLogoUrl = user?.logoUrl || null;
    const attendanceConfig = tenantDoc?.attendanceConfig || user?.attendanceConfig || null;
    const attendanceEnabled = !!attendanceConfig?.configured && Array.isArray(attendanceConfig?.branches) && attendanceConfig.branches.length > 0;

    const openProfile = () => {
        setProfileOpen(true);
    };



    const {
        notifications,
        loading: notificationsLoading,
        unreadCount: notificationCount,
        markOneRead,
        markAllRead,
    } = useTenantNotifications({ tenantId, userUid });

    // Icon mapping for menu items
    const iconMap = {
        '/dashboard': <DashboardOutlined />,
        '/student/dashboard': <DashboardOutlined />,
        '/student/profile': <UserOutlined />,
        '/student/attendance': <CalendarOutlined />,
        '/student/homeworks': <FileTextOutlined />,
        '/student/timetable': <ClockCircleOutlined />,
        '/student/fees': <DollarOutlined />,
        '/tenant/dashboard': <DashboardOutlined />,
        '/tenant/students': <TeamOutlined />,
        '/tenant/teachers': <TeamOutlined />,
        '/tenant/staff': <TeamOutlined />,
        '/tenant/classes-subjects': <DashboardOutlined />,
        '/tenants': <TeamOutlined />,
        '/plans': <TagOutlined />,
        '/modules': <AppstoreOutlined />,
        '/subscription-plans': <TagOutlined />,
        '/announcements': <NotificationOutlined />,
        '/support-tickets': <CustomerServiceOutlined />,
        '/leads': <UserOutlined />,
        '/tenant/leads': <UserOutlined />,
        '/quotes': <FileTextOutlined />,
        '/tenant/quotes': <FileTextOutlined />,
        '/orders': <ShoppingOutlined />,
        '/tenant/orders': <ShoppingOutlined />,
        '/dispatch': <InboxOutlined />,
        '/tenant/dispatch': <InboxOutlined />,
        '/customers': <TeamOutlined />,
        '/tenant/customers': <TeamOutlined />,
        '/carriers': <CarOutlined />,
        '/tenant/carriers': <CarOutlined />,
        '/billings': <DollarOutlined />,
        '/tenant/billings': <DollarOutlined />,
        '/reports': <BarChartOutlined />,
        '/tenant/reports': <BarChartOutlined />,
        '/profile': <UserOutlined />,
        '/tenant/profile': <UserOutlined />,
        '/settings': <SettingOutlined />,
        '/tenant/settings': <SettingOutlined />,
        '/access-manager': <SettingOutlined />,
        '/tenant/school-time': <ClockCircleOutlined />,
        '/tenant/timetable': <CalendarOutlined />,
        '/tenant/attendance/students': <CalendarOutlined />,
        '/tenant/attendance/teachers': <CalendarOutlined />,
        '/tenant/attendance/staff': <CalendarOutlined />,
        '/tenant/finance/expense-ledger': <DollarOutlined />,
        '/tenant/finance/receive-ledger': <DollarOutlined />,
        '/tenant/finance/transaction-ledger': <DollarOutlined />,
        '/tenant/finance/accounts': <BankOutlined />,
        '/tenant/fees-management': <BankOutlined />,
        '/tenant/notices-announcements': <NotificationOutlined />,
        '/tenant/homeworks': <FileTextOutlined />,
    };

    const menuItemsData = useMemo(() => {
        return getMenuItemsByRole(effectiveRole);
    }, [effectiveRole]);

    // Transform menu items data to Ant Design format with icons
    const menuItems = useMemo(() => {
        if (effectiveRole === 'student') {
            return menuItemsData.map((item) => ({
                key: item.key,
                icon: iconMap[item.key] || <DashboardOutlined />,
                label: item.label,
                disabled: false,
            }));
        }

        // Only keep professional top-level navigation
        const perms = Array.isArray(permissions)
            ? permissions
            : Array.isArray(user?.permissions)
                ? user.permissions
                : [];
        const has = (p) => perms.includes('*') || perms.includes(p) || effectiveRole === 'tenant' || user?.role === 'tenant' || user?.staffRole === 'admin';

        const isModuleDisabled = (moduleKey) => {
            const disabledModules = tenantDoc?.disabledModules || [];
            return disabledModules.includes(moduleKey);
        };

        const canStudentAttendance = (has('studentAttendance.view') || has('studentAttendance.full')) && !isModuleDisabled('attendance_students');
        const canTeacherAttendance = (has('teacherAttendance.view') || has('teacherAttendance.full')) && !isModuleDisabled('attendance_teachers');
        const canStaffAttendance = (has('staffAttendance.view') || has('staffAttendance.full')) && !isModuleDisabled('attendance_staff');
        const showAttendanceMenu = canStudentAttendance || canTeacherAttendance || canStaffAttendance;
        const canFinanceAccounts = (has('financeAccounts.view') || has('financeAccounts.full')) && !isModuleDisabled('finance_accounts');
        const canTransactionLedger = (has('transactionLedger.view') || has('transactionLedger.full')) && !isModuleDisabled('finance_transaction');
        const canReceiveLedger = (has('receiveLedger.view') || has('receiveLedger.full')) && !isModuleDisabled('finance_receive');
        const canExpenseLedger = (has('expenseLedger.view') || has('expenseLedger.full')) && !isModuleDisabled('finance_expense');
        const showFinanceMenu = canFinanceAccounts || canTransactionLedger || canReceiveLedger || canExpenseLedger;
        const canFeesManagement = (has('feesManagement.view') || has('feesManagement.full')) && !isModuleDisabled('fees');

        const allowedKeys =
            effectiveRole === 'tenant'
                ? [
                    '/tenant/dashboard',
                    ...((has('students.view') || has('students.full')) && !isModuleDisabled('students') ? ['/tenant/students'] : []),
                    ...((has('teachers.view') || has('teachers.full')) && !isModuleDisabled('teachers') ? ['/tenant/teachers'] : []),
                    ...((has('staff.view') || has('staff.full')) && !isModuleDisabled('staff') ? ['/tenant/staff'] : []),
                    ...((has('classes.view') || has('classes.full')) && !isModuleDisabled('classes') ? ['/tenant/classes-subjects'] : []),
                    ...((has('schoolTime.view') || has('schoolTime.full')) && !isModuleDisabled('schoolTime') ? ['/tenant/school-time'] : []),
                    ...(!isModuleDisabled('notices') ? ['/tenant/notices-announcements'] : []),
                    ...(!isModuleDisabled('homeworks') ? ['/tenant/homeworks'] : []),
                    ...(!isModuleDisabled('timetable') ? ['/tenant/timetable'] : []),
                ]
                : ['/dashboard', '/tenants', '/plans', '/modules'];
        const ownerAllowedKeys = ['/dashboard', '/tenants', '/plans', '/modules', '/settings'];
        const finalAllowedKeys = effectiveRole === 'tenant' ? allowedKeys : ownerAllowedKeys;
        const filteredItems = menuItemsData.filter((item) => finalAllowedKeys.includes(item.key));

        const base = filteredItems.map((item) => {
            const isTimetable = item.key === '/tenant/timetable';
            const isDisabled = isTimetable ? !attendanceEnabled : item.disabled;
            return {
                key: item.key,
                icon: iconMap[item.key] || <DashboardOutlined />,
                label: (isTimetable && isDisabled) ? (
                    <Tooltip title="Configure School Time first to enable Time Table" placement="right">
                        <span style={{ display: 'inline-block', width: '100%' }}>{item.label}</span>
                    </Tooltip>
                ) : item.label,
                disabled: isDisabled,
            };
        });

        if (effectiveRole !== 'tenant') return base;

        const next = [...base];

        if (showAttendanceMenu) {
            next.push({
                key: 'attendance',
                icon: <CalendarOutlined />,
                label: 'Attendance',
                disabled: false,
                children: [
                    ...(canStudentAttendance
                        ? [{
                            key: '/tenant/attendance/students',
                            icon: iconMap['/tenant/attendance/students'],
                            label: 'Student',
                            disabled: false,
                        }]
                        : []),
                    ...(canTeacherAttendance
                        ? [
                            {
                                key: '/public/attendance',
                                icon: <AppstoreOutlined />,
                                label: 'Quick Kiosk View',
                                disabled: false,
                            },
                            {
                                key: '/tenant/attendance/teachers',
                                icon: iconMap['/tenant/attendance/teachers'],
                                label: 'Teacher Monthly Grid',
                                disabled: false,
                            },
                        ]
                        : []),
                    ...(canStaffAttendance
                        ? [{
                            key: '/tenant/attendance/staff',
                            icon: iconMap['/tenant/attendance/staff'],
                            label: 'Staff',
                            disabled: false,
                        }]
                        : []),
                ],
            });
        }

        if (showFinanceMenu) {
            next.push({
                key: 'finance',
                icon: <DollarOutlined />,
                label: 'Finance',
                children: [
                    ...(canFinanceAccounts
                        ? [{
                            key: '/tenant/finance/accounts',
                            icon: iconMap['/tenant/finance/accounts'],
                            label: 'Account Management',
                        }]
                        : []),
                    ...(canTransactionLedger
                        ? [{
                            key: '/tenant/finance/transaction-ledger',
                            icon: iconMap['/tenant/finance/transaction-ledger'],
                            label: 'Transaction Ledger',
                        }]
                        : []),
                    ...(canReceiveLedger
                        ? [{
                            key: '/tenant/finance/receive-ledger',
                            icon: iconMap['/tenant/finance/receive-ledger'],
                            label: 'Receive Ledger',
                        }]
                        : []),
                    ...(canExpenseLedger
                        ? [{
                            key: '/tenant/finance/expense-ledger',
                            icon: iconMap['/tenant/finance/expense-ledger'],
                            label: 'Expense Ledger',
                        }]
                        : []),
                ],
            });
        }
        if (canFeesManagement) {
            next.push({
                key: '/tenant/fees-management',
                icon: iconMap['/tenant/fees-management'],
                label: 'Fees Management',
            });
        }
        return next;
    }, [menuItemsData, effectiveRole, permissions, user?.permissions, attendanceEnabled, tenantDoc]);

    const isCurrentRouteDisabled = useMemo(() => {
        if (effectiveRole !== 'tenant') return false;
        const disabledModules = tenantDoc?.disabledModules || [];
        const currentPath = location.pathname;
        const moduleObj = TENANT_MODULES.find(m => m.paths.some(p => currentPath === p || currentPath.startsWith(p + '/')));
        return moduleObj && disabledModules.includes(moduleObj.key);
    }, [location.pathname, tenantDoc, effectiveRole]);


    // User dropdown menu (avatar only in header; show details inside dropdown)
    const userMenuItems = [
        {
            key: 'meta',
            disabled: true,
            label: (
                <UserMenuMeta>
                    <UserMenuEmail>{getUserEmail()}</UserMenuEmail>
                    <UserMenuRole>{getUserRole()}</UserMenuRole>
                </UserMenuMeta>
            ),
        },
        { type: 'divider' },
        {
            key: 'profile',
            icon: <UserOutlined />,
            label: 'Profile',
        },
        {
            key: 'settings',
            icon: <SettingOutlined />,
            label: 'Settings',
        },
        {
            key: 'logout',
            icon: <LogoutOutlined />,
            label: 'Logout',
            danger: true,
        },
    ];

    const handleUserMenuClick = ({ key }) => {
        if (key === 'profile') {
            setUserDropdownOpen(false);
            openProfile();
            return;
        }
        if (key === 'settings') {
            navigate(effectiveRole === 'tenant' ? '/tenant/settings' : '/settings');
            return;
        }
        if (key === 'logout') {
            logout();
            navigate(getDefaultGuestSignInPath());
        }
    };

    const notificationRows = useMemo(() => notifications || [], [notifications]);

    // Get current page title
    const getPageTitle = () => {
        // Use effective role (tenant if on tenant route, otherwise user role)
        const routes = effectiveRole ? getRoutesByRole(effectiveRole) : getRoutesByRole('tenant');
        const currentRoute = routes.find(route => route.path === location.pathname);
        if (currentRoute?.menuItem?.label) return currentRoute.menuItem.label;
        if (location.pathname === '/tenant/school-time') return 'School Time Configuration';
        if (location.pathname === '/tenant/attendance/students') return 'Student';
        if (location.pathname === '/tenant/attendance/teachers') return 'Teacher';
        if (location.pathname === '/tenant/attendance/staff') return 'Staff';
        if (location.pathname === '/tenant/finance/transaction-ledger') return 'Transaction Ledger';
        if (location.pathname === '/tenant/finance/receive-ledger') return 'Receive Ledger';
        if (location.pathname === '/tenant/finance/expense-ledger') return 'Expense Ledger';
        if (location.pathname === '/tenant/finance/accounts') return 'Account Management';
        if (location.pathname === '/tenant/fees-management') return 'Fees Management';
        if (location.pathname === '/tenant/notices-announcements') return 'Notices & Announcements';
        if (location.pathname === '/tenant/homeworks') return 'Homework Diary';
        return 'Dashboard';
    };

    // Handle menu click
    const handleMenuClick = ({ key }) => {
        navigate(key);
        if (isMobile) {
            setDrawerOpen(false);
        }
    };

    // Get selected menu key
    const selectedKeys = [location.pathname];

    // Keep Finance/Attendance submenu expanded when inside it
    useEffect(() => {
        const p = location.pathname || '';
        const root =
            p.startsWith('/tenant/attendance/')
                ? 'attendance'
                : p.startsWith('/tenant/finance/')
                    ? 'finance'
                    : null;
        setOpenKeys(root ? [root] : []);
    }, [location.pathname]);

    // Check if screen is tablet or mobile
    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 1024);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Fixed sidebar width (no collapse/expand)
    const sidebarWidth = isMobile ? 0 : 220;

    return (
        <LayoutContainer>
            {/* Fixed Sidebar */}
            <Sider
                trigger={null}
                collapsible
                collapsed={false}
                width={220}
                collapsedWidth={0}
                breakpoint="lg"
                onBreakpoint={(broken) => {
                    if (broken) setIsMobile(true);
                }}
                style={{
                    overflow: 'hidden',
                    height: '100vh',
                    position: 'fixed',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    zIndex: 100,
                    display: isMobile ? 'none' : 'block',
                }}
                className="main-sidebar"
            >
                <SiderWrapper>
                    <StickyTopSection>
                        <LogoContainer
                            style={{ cursor: 'pointer' }}
                            onClick={openProfile}
                            title="View school profile"
                        >
                            <LogoImage
                                src={schoolLogoUrl || "/edulink-mark.png"}
                                alt="EDULINK logo"
                            />
                            <SchoolBrandBlock>
                                <SchoolNameText title={schoolName}>{schoolName}</SchoolNameText>
                                <SchoolMetaRow>
                                    <SchoolRegionText title={schoolRegion}>{schoolRegion}</SchoolRegionText>
                                </SchoolMetaRow>
                            </SchoolBrandBlock>
                        </LogoContainer>

                        <PlanCard>
                            <PlanCardMeta>
                                <PlanCardLabel>Plan</PlanCardLabel>
                                <PlanCardName title={schoolPlan}>{schoolPlan}</PlanCardName>
                            </PlanCardMeta>
                            <PlanUpgradeButton type="button" onClick={() => setUpgradeOpen(true)}>
                                Upgrade
                            </PlanUpgradeButton>
                        </PlanCard>
                    </StickyTopSection>

                    <MenuContainer>
                        <Menu
                            mode="inline"
                            selectedKeys={selectedKeys}
                            items={menuItems}
                            onClick={handleMenuClick}
                            openKeys={openKeys}
                            onOpenChange={setOpenKeys}
                        />

                    </MenuContainer>

                    <SidebarFooter>
                        <FooterBrandRow>
                            <FooterBrandLogo src="/edulink-mark.png" alt="EDULINK logo" />
                            <FooterBrandText>
                                <FooterBrandTitle>EduLink Management</FooterBrandTitle>
                                <FooterVersionText>v {APP_VERSION}</FooterVersionText>
                            </FooterBrandText>
                        </FooterBrandRow>
                    </SidebarFooter>
                </SiderWrapper>
            </Sider>

            {/* Main Content Area - Separate from sidebar */}
            <MainContentArea $sidebarWidth={sidebarWidth} $isMobile={isMobile}>
                {/* Sticky Header - Outside scroll */}
                <HeaderWrapper $isScrolled={isScrolled} $isMobile={isMobile}>
                    {isMobile ? (
                        <MobileDrawerButton onClick={() => setDrawerOpen(true)}>
                            <MenuOutlined />
                        </MobileDrawerButton>
                    ) : (
                        <div />
                    )}
                    <HeaderContent $isMobile={isMobile}>
                        <HeaderTitle $isMobile={isMobile}>{getPageTitle()}</HeaderTitle>
                    </HeaderContent>
                    <HeaderActions $isMobile={isMobile}>
                        <Dropdown
                            trigger={['click']}
                            placement="bottomRight"
                            open={notificationDropdownOpen}
                            onOpenChange={setNotificationDropdownOpen}
                            dropdownRender={() => (
                                <div
                                    style={{
                                        width: 360,
                                        maxWidth: '92vw',
                                        maxHeight: 420,
                                        overflow: 'auto',
                                        borderRadius: 12,
                                        border: '1px solid rgba(15, 23, 42, 0.12)',
                                        background: '#fff',
                                        boxShadow: '0 12px 30px rgba(15, 23, 42, 0.14)',
                                        padding: 10,
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            marginBottom: 8,
                                            padding: '2px 4px 8px',
                                            borderBottom: '1px solid rgba(15, 23, 42, 0.08)',
                                        }}
                                    >
                                        <div style={{ fontSize: 14, fontWeight: 600 }}>Notifications</div>
                                        <Button
                                            type="link"
                                            size="small"
                                            disabled={notificationCount === 0}
                                            onClick={() => void markAllRead()}
                                            style={{ paddingInline: 4 }}
                                        >
                                            Mark all read
                                        </Button>
                                    </div>

                                    {notificationsLoading ? (
                                        <div style={{ display: 'flex', justifyContent: 'center', padding: '18px 0' }}>
                                            <Spin size="small" />
                                        </div>
                                    ) : notificationRows.length === 0 ? (
                                        <div style={{ padding: '8px 0' }}>
                                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No notifications yet" />
                                        </div>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                            {notificationRows.map((row) => {
                                                const isRead = Array.isArray(row.readBy) && row.readBy.includes(userUid);
                                                return (
                                                    <div
                                                        key={row.id}
                                                        style={{
                                                            border: '1px solid rgba(138, 152, 167, 0.28)',
                                                            borderRadius: 10,
                                                            padding: 10,
                                                            background: isRead ? '#fff' : 'rgba(240, 74, 74, 0.04)',
                                                        }}
                                                    >
                                                        <div style={{ fontSize: 13, color: '#0f172a', marginBottom: 4 }}>
                                                            {row.title || 'Notice'}
                                                        </div>
                                                        <div
                                                            style={{
                                                                fontSize: 12,
                                                                color: 'rgba(15, 23, 42, 0.65)',
                                                                marginBottom: 8,
                                                                display: '-webkit-box',
                                                                WebkitLineClamp: 2,
                                                                WebkitBoxOrient: 'vertical',
                                                                overflow: 'hidden',
                                                            }}
                                                        >
                                                            {row.body || 'New announcement has been published.'}
                                                        </div>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <Tag color={isRead ? 'default' : 'red'} style={{ margin: 0 }}>
                                                                {isRead ? 'Read' : 'Unread'}
                                                            </Tag>
                                                            {!isRead ? (
                                                                <Button
                                                                    type="link"
                                                                    size="small"
                                                                    style={{ padding: 0 }}
                                                                    onClick={() => void markOneRead(row.id)}
                                                                >
                                                                    Mark as read
                                                                </Button>
                                                            ) : null}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}
                        >
                            <HeaderIconButton type="button" aria-label="Notifications">
                                {notificationCount > 0 ? (
                                    <Badge count={notificationCount} size="small">
                                        <NotificationOutlined />
                                    </Badge>
                                ) : (
                                    <NotificationOutlined />
                                )}
                            </HeaderIconButton>
                        </Dropdown>
                        <Dropdown
                            menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
                            placement="bottomRight"
                            trigger={['click']}
                            open={userDropdownOpen}
                            onOpenChange={setUserDropdownOpen}
                        >
                            <HeaderUserButton type="button">
                                <UserAvatar $collapsed={false}>{getUserInitials()}</UserAvatar>
                            </HeaderUserButton>
                        </Dropdown>
                    </HeaderActions>
                </HeaderWrapper>
                <HeaderDivider />

                {/* Scrollable Body Container */}
                <BodyContainer ref={bodyContainerRef}>
                    <ContentWrapper>
                        {isCurrentRouteDisabled ? <NotAuthorized /> : children}
                    </ContentWrapper>
                </BodyContainer>
            </MainContentArea>

            {/* Mobile Drawer */}
            {isMobile && (
                <>
                    <DrawerOverlay $isOpen={drawerOpen} onClick={() => setDrawerOpen(false)} />
                    <MobileDrawer $isOpen={drawerOpen}>
                        <DrawerContent>
                            <DrawerHeader>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                                    <LogoImage
                                        src="/edulink-mark.png"
                                        alt="EDULINK logo"
                                    />
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                                        <div style={{ fontWeight: 700, fontSize: 14, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 180 }}>
                                            {schoolName}
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, lineHeight: 1.1, color: 'rgba(15, 23, 42, 0.6)' }}>
                                            <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 180 }}>
                                                {schoolRegion}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <DrawerCloseButton onClick={() => setDrawerOpen(false)}>
                                    <CloseOutlined />
                                </DrawerCloseButton>
                            </DrawerHeader>

                            <PlanCard>
                                <PlanCardMeta>
                                    <PlanCardLabel>Plan</PlanCardLabel>
                                    <PlanCardName title={schoolPlan}>{schoolPlan}</PlanCardName>
                                </PlanCardMeta>
                                <PlanUpgradeButton
                                    type="button"
                                    onClick={() => {
                                        setUpgradeOpen(true);
                                        setDrawerOpen(false);
                                    }}
                                >
                                    Upgrade
                                </PlanUpgradeButton>
                            </PlanCard>

                            <MenuContainer>
                                <Menu
                                    mode="inline"
                                    selectedKeys={selectedKeys}
                                    items={menuItems}
                                    onClick={handleMenuClick}
                                    openKeys={openKeys}
                                    onOpenChange={setOpenKeys}
                                />
                            </MenuContainer>

                            <DrawerFooter>
                                <SidebarFooter>
                                    <FooterBrandRow>
                                        <FooterBrandLogo src="/edulink-mark.png" alt="EDULINK logo" />
                                        <FooterBrandText>
                                            <FooterBrandTitle>EduLink Management</FooterBrandTitle>
                                            <FooterVersionText>v {APP_VERSION}</FooterVersionText>
                                        </FooterBrandText>
                                    </FooterBrandRow>
                                </SidebarFooter>
                            </DrawerFooter>
                        </DrawerContent>
                    </MobileDrawer>
                </>
            )}

            <Drawer
                title="School profile"
                placement="right"
                open={profileOpen}
                onClose={() => setProfileOpen(false)}
                width={effectiveRole === 'tenant' ? 520 : 420}
                destroyOnClose
                styles={{ body: { paddingTop: 12 } }}
            >
                {effectiveRole === 'tenant' ? (
                    <SchoolProfilePanel variant="drawer" />
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        <div
                            style={{
                                width: '100%',
                                borderRadius: 14,
                                border: '1px solid rgba(15, 23, 42, 0.08)',
                                background: 'linear-gradient(135deg, rgba(240, 74, 74, 0.10), rgba(255, 255, 255, 0.0))',
                                padding: 14,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                            }}
                        >
                            <img
                                src={schoolLogoUrl || '/edulink-mark.png'}
                                alt="School logo"
                                style={{ width: 56, height: 56, objectFit: 'contain', borderRadius: 12, background: '#fff' }}
                            />
                            <div style={{ minWidth: 0 }}>
                                <div
                                    style={{
                                        fontWeight: 900,
                                        fontSize: 16,
                                        lineHeight: 1.2,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        maxWidth: 300,
                                    }}
                                >
                                    {schoolName}
                                </div>
                                <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                    <Tag color="red">{schoolPlan}</Tag>
                                    <Tag>{schoolRegion}</Tag>
                                </div>
                            </div>
                        </div>

                        <Form
                            layout="vertical"
                            initialValues={{
                                schoolName,
                                plan: schoolPlan,
                                region: schoolRegion,
                                phone: user?.phone || '',
                                address: user?.address || '',
                                adminEmail: user?.adminEmail || user?.email || '',
                            }}
                        >
                            <Form.Item label="School Name" name="schoolName">
                                <Input readOnly />
                            </Form.Item>
                            <Form.Item label="Plan" name="plan">
                                <Input readOnly />
                            </Form.Item>
                            <Form.Item label="Region" name="region">
                                <Input readOnly />
                            </Form.Item>
                            <Form.Item label="Phone" name="phone">
                                <Input readOnly placeholder="—" />
                            </Form.Item>
                            <Form.Item label="Address" name="address">
                                <Input.TextArea readOnly placeholder="—" autoSize={{ minRows: 2, maxRows: 4 }} />
                            </Form.Item>
                            <Form.Item label="Admin Email" name="adminEmail">
                                <Input readOnly />
                            </Form.Item>
                        </Form>
                    </div>
                )}
            </Drawer>

            <Modal
                title="Upgrade"
                open={upgradeOpen}
                onCancel={() => setUpgradeOpen(false)}
                footer={[
                    <Button key="close" onClick={() => setUpgradeOpen(false)}>
                        Close
                    </Button>,
                ]}
            >
                <div style={{ fontWeight: 700, marginBottom: 8 }}>Upgraded Plan</div>
                <div style={{ color: 'rgba(15, 23, 42, 0.75)' }}>Upcoming in future.</div>
            </Modal>
        </LayoutContainer>
    );
};

export default Inside;
